import React from "react";
import { useClientSideSplitTreatments } from "@leafly-com/split-next";

import { CATEGORY_NAMES } from "constants/dispensary";
import { Action, Category } from "constants/events";
import { MenuItem } from "custom-types/MenuItem";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useSearch from "hooks/useSearch";
import { useSelector } from "redux/hooks";
import { getUserId } from "redux/selectors/user";
import { dispensaryHref } from "utils/dispensaryUrls";
import { getRoundedDistance } from "utils/distance";
import { localizedDistance } from "utils/localizedDistance";
import { getSplitKey } from "utils/split/getSplitKey";

import Button from "components/botanic/Button";
import CoinList from "components/CoinList";
import DealsCarousel from "components/Shared/DealsCarousel";
import MenuItemCarousel from "components/Shared/MenuItemCarousel";
import MenuItemCarouselPlaceholder from "components/Shared/MenuItemCarousel/MenuItemCarouselPlaceholder";

const MenuItemResults: React.FC = () => {
  const countryCode = useDomainCountryCode();
  const { menuItemDispensaries, searchTerm, loading } = useSearch();

  const displayCategories = Object.keys(CATEGORY_NAMES);

  const { publishEvent } = useEventTracker();
  const userId = useSelector(getUserId);

  const trackHeaderLinkClick = React.useCallback(
    (dispensaryId: number, label: string, distanceMi?: number) => {
      publishEvent({
        action: Action.click,
        category: Category.menuItemCardTray,
        // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
        dispensaryDistance: getRoundedDistance(distanceMi, countryCode),
        dispensaryId,
        label,
        userId,
      });
    },
    [userId],
  );

  const trackMenuItemCardClick = React.useCallback(
    (menuItem: MenuItem, label = "") => {
      publishEvent({
        action: Action.click,
        brandId: menuItem.brand?.id,
        category: Category.menuItemCard,
        dispensaryId: menuItem.dispensary.id,
        label,
        menuItemId: menuItem.menuItemId,
        productId: menuItem.product?.id,
        strainId: menuItem.strain?.id,
        userId: userId,
      });
    },
    [userId],
  );

  const handleViewDetailsClick = (menuItem: MenuItem) => {
    trackMenuItemCardClick(menuItem, "view details button");
  };

  const split = useClientSideSplitTreatments(
    ["EL-180_searchToShopcore_test"],
    getSplitKey(),
  );
  const useShopcoreMenu =
    !split.isLoading &&
    split.treatments["EL-180_searchToShopcore_test"].treatment === "on";

  return (
    <>
      <h2>Products</h2>
      <p className="mb-lg">matching "{searchTerm}"</p>

      {loading ? (
        <MenuItemCarouselPlaceholder />
      ) : (
        menuItemDispensaries.map(
          ({
            city,
            deliveryEnabled,
            distanceMi,
            id,
            name,
            pickupEnabled,
            slug,
            state,
            retailType,
          }) => {
            const distance = localizedDistance(distanceMi, countryCode);
            const showDistance = !deliveryEnabled || pickupEnabled;
            const showOnlineOrdering = deliveryEnabled || pickupEnabled;
            const subHeader =
              distance && showDistance
                ? `${distance} away - ${city}, ${state}`
                : `${city}, ${state}`;

            const dispensaryLink = dispensaryHref({
              additionalPath: `/menu?q=${searchTerm}#products`,
              countryCode,
              retailType,
              slug,
            });

            return (
              <MenuItemCarousel
                actionHref={dispensaryLink}
                actionOnClick={() =>
                  trackHeaderLinkClick(
                    id,
                    "show all matching products link",
                    distanceMi,
                  )
                }
                actionText={"Show %tc matching products"}
                cardOnClick={trackMenuItemCardClick}
                cardsPerSlide={4}
                className="mb-xl"
                header={name}
                headerLink={dispensaryLink}
                headerOnClick={() =>
                  trackHeaderLinkClick(id, "retailer header link", distanceMi)
                }
                key={`${name}-${searchTerm}`}
                query={searchTerm}
                showOnlineOrdering={showOnlineOrdering}
                slug={slug}
                sort={{ 0: { serp_shop: "asc" } }}
                subheader={subHeader}
                viewDetailsOnClick={handleViewDetailsClick}
              />
            );
          },
        )
      )}

      <Button
        data-testid="shop-nearby-menus"
        className="mb-section"
        href={
          useShopcoreMenu
            ? `/shop?fulfillmentPreference=pickup&q=${searchTerm}`
            : `/search/shop?q=${searchTerm}`
        }
        onClick={() =>
          publishEvent({
            action: Action.click,
            category: Category.shopAllMenus,
            label: "shop all nearby menus",
          })
        }
        variant="secondary"
      >
        view all product results
      </Button>

      {!!displayCategories.length && (
        <>
          <h2 className="mb-sm">Shop products by category</h2>
          <CoinList className="mb-section" coins={displayCategories} />
        </>
      )}

      <DealsCarousel />
    </>
  );
};

export default MenuItemResults;
